import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, Coffee, Users, Calendar, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SeriesSkippedDialog
 * Ersatz für den unleserlichen 8s-Toast bei Serien-Buchungen.
 * Gruppiert übersprungene Termine nach Grund, sortiert chronologisch
 * und bietet eine Kopier-Aktion für den Admin.
 *
 * Erwartetes `info`-Schema (vom Backend `_series_info`):
 *   {
 *     created_count: number,
 *     total_requested: number,
 *     skipped_count: number,
 *     skipped_appointments: [
 *       { date: ISOString, reason: 'full'|'conflict'|'mindbody', reason_text: string, conflicting_service: string }
 *     ]
 *   }
 */
const REASON_META = {
  conflict: { icon: Calendar, label: 'Belegt', color: 'text-amber-700 bg-amber-50 border-amber-200' },
  full: { icon: Users, label: 'Voll', color: 'text-orange-700 bg-orange-50 border-orange-200' },
  mindbody: { icon: AlertCircle, label: 'Mindbody', color: 'text-blue-700 bg-blue-50 border-blue-200' },
  break: { icon: Coffee, label: 'Pause', color: 'text-zinc-700 bg-zinc-50 border-zinc-200' },
  default: { icon: AlertCircle, label: 'Sonstiges', color: 'text-zinc-700 bg-zinc-50 border-zinc-200' },
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return {
      dateStr: d.toLocaleDateString('de-CH', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }),
      timeStr: d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }),
      sortKey: d.getTime(),
    };
  } catch {
    return { dateStr: iso, timeStr: '', sortKey: 0 };
  }
}

export default function SeriesSkippedDialog({ open, onOpenChange, info }) {
  const groups = useMemo(() => {
    if (!info?.skipped_appointments?.length) return [];
    const byReason = {};
    info.skipped_appointments.forEach((item) => {
      const reasonKey = item.reason || 'default';
      if (!byReason[reasonKey]) byReason[reasonKey] = [];
      byReason[reasonKey].push({ ...item, ...formatDate(item.date) });
    });
    // Sortiere innerhalb jeder Gruppe chronologisch
    Object.values(byReason).forEach((arr) =>
      arr.sort((a, b) => a.sortKey - b.sortKey)
    );
    // Stabile Reihenfolge der Gruppen
    const order = ['conflict', 'full', 'mindbody', 'break', 'default'];
    return order
      .filter((k) => byReason[k])
      .map((k) => ({ key: k, items: byReason[k], meta: REASON_META[k] || REASON_META.default }));
  }, [info]);

  const handleCopy = () => {
    if (!info?.skipped_appointments?.length) return;
    const lines = [...info.skipped_appointments]
      .map((s) => ({ ...s, ...formatDate(s.date) }))
      .sort((a, b) => a.sortKey - b.sortKey)
      .map((s) => `${s.dateStr} ${s.timeStr} — ${s.reason_text || s.reason}`);
    const text = `${info.created_count} von ${info.total_requested} Terminen erstellt — ${info.skipped_count} übersprungen:\n\n${lines.join('\n')}`;
    navigator.clipboard.writeText(text).then(
      () => toast.success('Liste in Zwischenablage kopiert'),
      () => toast.error('Kopieren fehlgeschlagen')
    );
  };

  if (!info) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="series-skipped-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Serien-Buchung abgeschlossen
          </DialogTitle>
          <DialogDescription>
            <span className="font-semibold text-emerald-700">{info.created_count}</span>
            {' von '}
            <span className="font-semibold">{info.total_requested}</span>
            {' Terminen wurden erstellt. '}
            <span className="font-semibold text-amber-700">{info.skipped_count}</span>
            {' wurden übersprungen — siehe unten.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2 space-y-4" data-testid="series-skipped-list">
          {groups.map(({ key, items, meta }) => {
            const Icon = meta.icon;
            return (
              <div key={key} className={`rounded-lg border ${meta.color} p-3`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-4 w-4" />
                  <span className="font-semibold text-sm">
                    {meta.label} · {items.length} Termin{items.length === 1 ? '' : 'e'}
                  </span>
                </div>
                <ul className="space-y-1 text-sm">
                  {items.map((it, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2 py-1"
                      data-testid={`series-skipped-item-${key}-${idx}`}
                    >
                      <span className="font-mono text-xs whitespace-nowrap text-zinc-600 pt-0.5">
                        {it.dateStr} {it.timeStr}
                      </span>
                      <span className="text-zinc-800">— {it.reason_text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={handleCopy}
            data-testid="series-skipped-copy-btn"
            className="gap-2"
          >
            <Copy className="h-4 w-4" />
            Liste kopieren
          </Button>
          <Button
            onClick={() => onOpenChange(false)}
            data-testid="series-skipped-close-btn"
          >
            Verstanden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
